<template>
    <settings-page :title="title" :description="description">
        <div class="colors">
            <ul class="colors__list">
                <li
                    v-for="color in categoryColors"
                    :key="color.name"
                    class="colors__item"
                    :style="colorItemStyle"
                >
                    <settings-color :color="color" @input="onUpdateColor" />
                </li>

                <li
                    v-if="categoryColors.length < maxColors"
                    class="colors__item colors__item--button"
                    :style="colorItemStyle"
                    @click="addColor"
                >
                    <button class="colors__add" type="button">
                        <font-awesome-icon class="colors__add-icon" :icon="['fal', 'plus']" />
                    </button>

                    <div class="colors__text">
                        <span v-text="addColorLabel" />
                    </div>
                </li>
            </ul>
        </div>
    </settings-page>
</template>

<script>
import { mapMutations, mapState } from 'vuex';

import SettingsPage from '@/components/settings/shared/settings-page';
import SettingsColor from '@/components/settings/color/settings-color';
import colorData from '@/data/constants/colors';
import { SHOW_SNACKBAR } from '@/store/types/mutation-types';

export default {
    name: 'settings-colors',
    components: { SettingsPage, SettingsColor },

    data() {
        return {
            description: `You may configure up to ${ colorData.MAX_NUMBER } custom brand colors of your choosing. Note that the color names are editable as well.`,
            addColorLabel: 'Add Color',
        };
    },

    computed: {
        ...mapState({
            settings: state => state.settings,
        }),

        colors() {
            if (!this.settings) return [];

            return this.settings.colors;
        },

        categoryColors() {
            return this.colors.filter((colorObject) => {
                return (colorObject.category === this.colorCategory && colorObject.active === true);
            });
        },

        colorItemStyle() {
            return {
                width: `${ 100 / colorData.numColumns }%`,
            };
        },

        maxColors() {
            return colorData.MAX_NUMBER;
        },
    },

    methods: {
        ...mapMutations({
            showSnackbar: SHOW_SNACKBAR,
        }),

        onUpdateColor({ name, displayName, value }) {
            const colorIndex = this.colors.findIndex(c => c.name === name);

            if (colorIndex < 0) {
                return console.warn(`Update failed. Unable to locate a color named "${ name }".`);
            }

            this.colors[colorIndex].displayName = displayName;
            this.colors[colorIndex].value = value;

            this.update();
        },

        update() {
            this.$emit('input', 'colors', this.colors);
        },

        addColor() {
            const firstInactiveIndex = this.colors.findIndex(c => c.active === false && c.category === this.colorCategory);

            if (firstInactiveIndex < 0) {
                this.showSnackbar({
                    title: 'Unable to add more colors.',
                    text: 'The maximum number of colors has been reached.',
                });

                return;
            }

            this.colors[firstInactiveIndex].active = true;

            this.update();
        },
    },
};
</script>

<style lang="scss">
.colors {
    &__list {
        display: flex;
        flex-wrap: wrap;
        margin-left: -2.5rem;
        margin-right: -2.5rem;
        max-width: 77rem;
    }

    &__item {
        padding-right: 15px;
        padding-bottom: 50px;
        transition: all 0.5s;

        &--button {
            align-items: center;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            opacity: .5;
            width: 160px;
            min-width: 160px;


            &:hover {
                opacity: 1;
            }
        }
    }

    &__add {
        background-color: rgba(255,255,255,0.5);
        border: 1px solid #edf2f7;
        border-radius: 50%;
        height: 72px;
        margin-bottom: 8px;
        width: 72px;

        &-icon {
            position: relative;
            top: 2px;
            font-size: 1.5rem;
        }
    }

    &__text {
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
    }
}
</style>
